// ─────────────────────────────────────────────────────────────────────────────
// Customer pricing — per-customer custom product prices, deposits, delivery
// charges + price tiers.
//
// A customer has EITHER an assigned price tier OR individual custom pricing
// (item prices/deposits and delivery overrides), never both — enforced by DB
// triggers. `fetchCustomerPricing` resolves the effective values either way;
// staff order/sale UIs and the online store apply them wherever lines are
// created. Prices are dollars (same unit as stock_items.price); deposits are
// cents (same unit as stock_items.deposit_cents); delivery overrides are
// dollars keyed by shipping method NAME.
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from './supabase';

export interface PriceTier {
  id: string;
  business_id: string;
  name: string;
  created_at: string;
  /** Shipping method name → custom delivery price (dollars). */
  delivery_prices?: Record<string, number>;
  items?: PriceTierItem[];
}

export interface PriceTierItem {
  id: string;
  tier_id: string;
  stock_item_id: string;
  price: number | null;
  /** Custom per-unit container deposit (cents); null = catalog, 0 = no deposit. */
  deposit_cents: number | null;
  /** Joined for display. */
  item_name?: string;
  catalog_price?: number | null;
  catalog_deposit_cents?: number | null;
}

export interface CustomerItemPrice {
  id: string;
  customer_id: string;
  stock_item_id: string;
  price: number | null;
  deposit_cents: number | null;
  item_name?: string;
  catalog_price?: number | null;
  catalog_deposit_cents?: number | null;
}

export interface CustomerPricing {
  /** stock_item_id → custom sale price (dollars). */
  prices: Record<string, number>;
  /** stock_item_id → custom deposit (cents; 0 = no deposit for this customer). */
  deposits: Record<string, number>;
  /** shipping method name → custom delivery price (dollars). */
  delivery: Record<string, number>;
}

export const EMPTY_PRICING: CustomerPricing = { prices: {}, deposits: {}, delivery: {} };

/** Effective pricing for a customer (their custom rows or their tier's). */
export async function fetchCustomerPricing(
  businessId: string,
  customerId: string
): Promise<CustomerPricing> {
  const [{ data: rows, error }, { data: delivery, error: deliveryError }] = await Promise.all([
    supabase.rpc('get_customer_prices', { p_business_id: businessId, p_customer_id: customerId }),
    supabase.rpc('get_customer_delivery_prices', { p_business_id: businessId, p_customer_id: customerId }),
  ]);
  if (error) throw error;
  if (deliveryError) throw deliveryError;
  const result: CustomerPricing = { prices: {}, deposits: {}, delivery: {} };
  for (const row of (rows || []) as Array<{ stock_item_id: string; price: number | null; deposit_cents: number | null }>) {
    if (row.price != null) result.prices[row.stock_item_id] = Number(row.price);
    if (row.deposit_cents != null) result.deposits[row.stock_item_id] = Number(row.deposit_cents);
  }
  if (delivery && typeof delivery === 'object') {
    for (const [name, price] of Object.entries(delivery as Record<string, unknown>)) {
      const p = Number(price);
      if (Number.isFinite(p) && p >= 0) result.delivery[name] = p;
    }
  }
  return result;
}

/** stock_item_id → effective custom sale price (dollars). Empty map = catalog pricing. */
export async function fetchCustomerPriceMap(
  businessId: string,
  customerId: string
): Promise<Record<string, number>> {
  return (await fetchCustomerPricing(businessId, customerId)).prices;
}

// ── Tiers ─────────────────────────────────────────────────────────────────────

export async function listPriceTiers(businessId: string): Promise<PriceTier[]> {
  const { data, error } = await supabase
    .from('price_tiers')
    .select('id, business_id, name, created_at, delivery_prices, items:price_tier_items(id, tier_id, stock_item_id, price, deposit_cents, item:stock_items(name, price, deposit_cents))')
    .eq('business_id', businessId)
    .order('name');
  if (error) throw error;
  return (data || []).map((t: any) => ({
    ...t,
    delivery_prices: (t.delivery_prices && typeof t.delivery_prices === 'object') ? t.delivery_prices : {},
    items: (t.items || [])
      .map((i: any) => ({
        id: i.id,
        tier_id: i.tier_id,
        stock_item_id: i.stock_item_id,
        price: i.price != null ? Number(i.price) : null,
        deposit_cents: i.deposit_cents != null ? Number(i.deposit_cents) : null,
        item_name: i.item?.name ?? 'Product',
        catalog_price: i.item?.price != null ? Number(i.item.price) : null,
        catalog_deposit_cents: i.item?.deposit_cents != null ? Number(i.item.deposit_cents) : null,
      }))
      .sort((a: PriceTierItem, b: PriceTierItem) => (a.item_name || '').localeCompare(b.item_name || '')),
  }));
}

export async function createPriceTier(businessId: string, name: string): Promise<PriceTier> {
  const { data, error } = await supabase
    .from('price_tiers')
    .insert({ business_id: businessId, name })
    .select('id, business_id, name, created_at')
    .single();
  if (error) throw error;
  return { ...(data as PriceTier), items: [] };
}

export async function renamePriceTier(tierId: string, name: string): Promise<void> {
  const { error } = await supabase.from('price_tiers').update({ name, updated_at: new Date().toISOString() }).eq('id', tierId);
  if (error) throw error;
}

/** Deleting a tier unassigns it from customers (FK SET NULL) and drops its items. */
export async function deletePriceTier(tierId: string): Promise<void> {
  const { error } = await supabase.from('price_tiers').delete().eq('id', tierId);
  if (error) throw error;
}

/** Upsert a tier item. Only the provided fields change; a row with both price
 *  and deposit cleared should be removed with removeTierItem instead. */
export async function setTierItemPrice(
  businessId: string,
  tierId: string,
  stockItemId: string,
  values: { price?: number | null; deposit_cents?: number | null }
): Promise<void> {
  const { error } = await supabase
    .from('price_tier_items')
    .upsert(
      { business_id: businessId, tier_id: tierId, stock_item_id: stockItemId, ...values },
      { onConflict: 'tier_id,stock_item_id' }
    );
  if (error) throw error;
}

/** Set the tier's delivery overrides (shipping method name → dollars). */
export async function setTierDeliveryPrices(tierId: string, delivery: Record<string, number>): Promise<void> {
  const { error } = await supabase
    .from('price_tiers')
    .update({ delivery_prices: delivery, updated_at: new Date().toISOString() })
    .eq('id', tierId);
  if (error) throw error;
}

export async function removeTierItem(tierId: string, stockItemId: string): Promise<void> {
  const { error } = await supabase
    .from('price_tier_items')
    .delete()
    .eq('tier_id', tierId)
    .eq('stock_item_id', stockItemId);
  if (error) throw error;
}

// ── Per-customer custom prices ────────────────────────────────────────────────

export async function listCustomerItemPrices(customerId: string): Promise<CustomerItemPrice[]> {
  const { data, error } = await supabase
    .from('customer_item_prices')
    .select('id, customer_id, stock_item_id, price, deposit_cents, item:stock_items(name, price, deposit_cents)')
    .eq('customer_id', customerId);
  if (error) throw error;
  return (data || [])
    .map((r: any) => ({
      id: r.id,
      customer_id: r.customer_id,
      stock_item_id: r.stock_item_id,
      price: r.price != null ? Number(r.price) : null,
      deposit_cents: r.deposit_cents != null ? Number(r.deposit_cents) : null,
      item_name: r.item?.name ?? 'Product',
      catalog_price: r.item?.price != null ? Number(r.item.price) : null,
      catalog_deposit_cents: r.item?.deposit_cents != null ? Number(r.item.deposit_cents) : null,
    }))
    .sort((a, b) => (a.item_name || '').localeCompare(b.item_name || ''));
}

/** Upsert a customer item price row. Only the provided fields change; a row
 *  with both price and deposit cleared should be removed instead. */
export async function setCustomerItemPrice(
  businessId: string,
  customerId: string,
  stockItemId: string,
  values: { price?: number | null; deposit_cents?: number | null }
): Promise<void> {
  const { error } = await supabase
    .from('customer_item_prices')
    .upsert(
      { business_id: businessId, customer_id: customerId, stock_item_id: stockItemId, ...values, updated_at: new Date().toISOString() },
      { onConflict: 'customer_id,stock_item_id' }
    );
  if (error) throw error;
}

export async function removeCustomerItemPrice(customerId: string, stockItemId: string): Promise<void> {
  const { error } = await supabase
    .from('customer_item_prices')
    .delete()
    .eq('customer_id', customerId)
    .eq('stock_item_id', stockItemId);
  if (error) throw error;
}

/** Assign (or clear with null) a tier. DB trigger rejects if custom prices exist. */
export async function assignCustomerTier(customerId: string, tierId: string | null): Promise<void> {
  const { error } = await supabase.from('customers').update({ price_tier_id: tierId }).eq('id', customerId);
  if (error) throw error;
}

/** Set the customer's own delivery overrides (shipping method name → dollars).
 *  DB trigger rejects a non-empty map while a tier is assigned. */
export async function setCustomerDeliveryPrices(
  customerId: string,
  delivery: Record<string, number>
): Promise<void> {
  const { error } = await supabase
    .from('customers')
    .update({ custom_delivery_prices: Object.keys(delivery).length > 0 ? delivery : null })
    .eq('id', customerId);
  if (error) throw error;
}

/** The customer's own delivery overrides (not tier-resolved — for the editor UI). */
export async function getCustomerDeliveryPrices(customerId: string): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from('customers')
    .select('custom_delivery_prices')
    .eq('id', customerId)
    .maybeSingle();
  if (error) throw error;
  const raw = data?.custom_delivery_prices;
  return raw && typeof raw === 'object' ? (raw as Record<string, number>) : {};
}

/** Apply delivery overrides to shipping methods: an override (matched by
 *  method name) replaces the base price, including area-rated pricing. */
export function applyDeliveryOverrides<T extends { name: string; price: number; pricing?: 'flat' | 'by_area'; areaRates?: unknown }>(
  methods: T[],
  delivery: Record<string, number>
): T[] {
  return methods.map(m => {
    const override = delivery[m.name];
    if (override == null || !Number.isFinite(Number(override))) return m;
    return { ...m, price: Number(override), pricing: 'flat' as const, areaRates: undefined };
  });
}
